/**
 * Role Assignment Dialog
 *
 * Modal for assigning or changing a user's role.
 * Shows the user, role selector, and capability preview.
 */

import type { SystemRoleName } from "@tatvex/types";
import { SYSTEM_ROLES } from "@tatvex/types";
import { Loader2, Shield } from "lucide-react";
import type { ReactElement } from "react";
import { useState, useCallback } from "react";

import { CapabilityViewer } from "./CapabilityViewer";

// ============================================================================
// Types
// ============================================================================

interface RoleAssignmentDialogProps {
  /** User to assign role to (null for new assignment from list) */
  readonly user: {
    readonly userId: string;
    readonly email: string;
    readonly name: string;
    readonly currentRole: string;
  } | null;
  readonly isLoading: boolean;
  readonly error?: string;
  readonly onSubmit: (userId: string, role: string) => void;
  readonly onCancel: () => void;
}

// ============================================================================
// Constants
// ============================================================================

const ROLE_OPTIONS = [
  {
    value: "viewer",
    label: "Viewer",
    description: "Read-only access to configurations and analytics",
  },
  { value: "editor", label: "Editor", description: "Can edit configurations and products" },
  { value: "publisher", label: "Publisher", description: "Can promote configurations to live" },
  { value: "admin", label: "Administrator", description: "Full access to tenant" },
] as const;

// ============================================================================
// Component
// ============================================================================

export function RoleAssignmentDialog({
  user,
  isLoading,
  error,
  onSubmit,
  onCancel,
}: RoleAssignmentDialogProps): ReactElement {
  const [selectedRole, setSelectedRole] = useState<string>(user?.currentRole ?? "viewer");

  const handleSubmit = useCallback((): void => {
    if (!user) return;
    onSubmit(user.userId, selectedRole);
  }, [user, selectedRole, onSubmit]);

  const capabilities =
    selectedRole in SYSTEM_ROLES ? SYSTEM_ROLES[selectedRole as SystemRoleName].capabilities : [];
  const isUnchanged = user?.currentRole === selectedRole;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="w-full max-w-lg rounded-lg border border-border bg-background p-6 shadow-xl">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 text-primary">
            <Shield className="h-5 w-5" />
          </div>
          <div>
            <h2 className="text-lg font-semibold">Assign Role</h2>
            {user && <p className="text-sm text-foreground-muted">{user.email}</p>}
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="mb-4 rounded-md border border-error/30 bg-error/5 p-3 text-sm text-error">
            {error}
          </div>
        )}

        {/* User Info */}
        {user && (
          <div className="mb-4 rounded-md border border-border bg-background-alt p-3">
            <p className="text-sm font-medium">{user.name}</p>
            <p className="text-xs text-foreground-muted">{user.email}</p>
            <p className="text-xs text-foreground-muted mt-1">
              Current role: <span className="font-medium">{user.currentRole}</span>
            </p>
          </div>
        )}

        {/* Role Selector */}
        <div className="mb-4">
          <label className="block text-sm font-medium mb-2" htmlFor="role-select">
            Select Role
          </label>
          <select
            id="role-select"
            value={selectedRole}
            onChange={(e) => setSelectedRole(e.target.value)}
            className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
            disabled={isLoading}
          >
            {ROLE_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label} - {option.description}
              </option>
            ))}
          </select>
        </div>

        {/* Capability Preview */}
        <div className="mb-6 rounded-md border border-border p-4">
          <h3 className="text-sm font-medium mb-3">Capabilities ({capabilities.length})</h3>
          <CapabilityViewer role={selectedRole} />
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3">
          <button onClick={onCancel} disabled={isLoading} className="btn btn-secondary">
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={isLoading || isUnchanged || !user}
            className="btn btn-primary"
          >
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Assigning...
              </>
            ) : (
              "Assign Role"
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
